import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Field, inputStyle } from '@/components/Field';
import { Heading } from '@/components/Heading';
import { PageHeader } from '@/components/PageHeader';
import { SkeletonList } from '@/components/Skeleton';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useAuth } from '@/features/auth/useAuth';
import {
  BeforeAfterPhotoSlots,
  type BeforeAfterPhotoValue,
} from '@/features/photos/BeforeAfterPhotoSlots';
import { InvoiceFlow } from '@/features/vehicles/InvoiceFlow';
import { useAddServiceEntry, type NewServiceEntry } from '@/hooks/useAddServiceEntry';
import { useServiceEntries } from '@/hooks/useServiceEntries';
import { useServiceQueue } from '@/hooks/useServiceQueue';
import {
  PHOTO_NOT_READY_MESSAGE,
  remove as removeQueued,
  type PendingPhotoRef,
} from '@/lib/serviceQueue';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Check } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';

// `z.union([z.coerce.number(), z.literal('')])` looks like it should pass an
// empty string through as `''`, but `z.coerce.number()` is tried FIRST and
// happily coerces `''` -> `0` (JS's own `Number('') === 0`) — it never
// falls through to the `z.literal('')` branch, so a cleared field silently
// submits `0` instead of "no value" (spec 011 Slice 2's "clears the field"
// row requires omitting `laborHours` entirely — this bug pre-dates Slice 2
// and affected `mileage` too). `z.preprocess` intercepts `''` -> `undefined`
// BEFORE coercion ever runs, so the output type is a clean `number |
// undefined` with no false-zero trap.
const emptyToUndefined = (val: unknown) => (val === '' ? undefined : val);

const ServiceEntryFormSchema = z.object({
  description: z.string().min(1, 'Required'),
  performedAt: z.string().min(1, 'Required'),
  mileage: z.preprocess(emptyToUndefined, z.coerce.number().int().nonnegative().optional()),
  performedBy: z.string().optional(),
  laborHours: z.preprocess(emptyToUndefined, z.coerce.number().nonnegative().optional()),
  // Spec 011 — independent of `source`/`EntrySource`; default off.
  selfPerformed: z.boolean().optional(),
});

/**
 * Spec 011 — splits a BeforeAfterPhotoValue slot into either a committed
 * photoId (ready for the POST body — lamport's DTO takes a plain id, the
 * server resolves it to {photoId, sha256} internally) or a `PendingPhotoRef`
 * (still resolving — see useServiceQueue.ts's drain). A slot already known
 * `committed` is always resolved; everything else defers to the drain.
 */
function toEntryPhotoFields(slots: BeforeAfterPhotoValue): {
  entryFields: Pick<NewServiceEntry, 'beforePhotoId' | 'afterPhotoId'>;
  pendingPhotoRefs: { before?: PendingPhotoRef; after?: PendingPhotoRef };
} {
  const entryFields: Pick<NewServiceEntry, 'beforePhotoId' | 'afterPhotoId'> = {};
  const pendingPhotoRefs: { before?: PendingPhotoRef; after?: PendingPhotoRef } = {};

  if (slots.before?.photoId && slots.before.committed) {
    entryFields.beforePhotoId = slots.before.photoId;
  } else if (slots.before?.photoId) {
    pendingPhotoRefs.before = { photoId: slots.before.photoId };
  } else if (slots.before?.clientId) {
    pendingPhotoRefs.before = { clientId: slots.before.clientId };
  }

  if (slots.after?.photoId && slots.after.committed) {
    entryFields.afterPhotoId = slots.after.photoId;
  } else if (slots.after?.photoId) {
    pendingPhotoRefs.after = { photoId: slots.after.photoId };
  } else if (slots.after?.clientId) {
    pendingPhotoRefs.after = { clientId: slots.after.clientId };
  }

  return { entryFields, pendingPhotoRefs };
}

type ServiceEntryFormInput = z.input<typeof ServiceEntryFormSchema>;
type ServiceEntryFormOutput = z.output<typeof ServiceEntryFormSchema>;

// Spec 011 Slice 2 — shared empty value so photoSlots always carries a
// `suggestion` tri-state (BeforeAfterPhotoValue.suggestion is required).
const EMPTY_PHOTO_SLOTS: BeforeAfterPhotoValue = {
  before: null,
  after: null,
  suggestion: { status: 'pending' },
};

/** "1.5" -> "1h 30m" / "0.25" -> "15m" / "2" -> "2h" — matches eames' copy. */
function formatHoursAsHm(hours: number): string {
  const totalMin = Math.round(hours * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Normalizes an RHF field value (raw input string, coerced number, or '') to a number or null. */
function toNumberOrNull(raw: unknown): number | null {
  if (raw === '' || raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function ServiceLog() {
  const { id: vehicleId = '' } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: entries, loading, error } = useServiceEntries(vehicleId);
  const { add, pending: addPending } = useAddServiceEntry();
  const { pending: queuedEntries, drain } = useServiceQueue(vehicleId);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showInvoiceFlow, setShowInvoiceFlow] = useState(false);
  // Ledger-first: form is hidden behind a button.
  const [showAddForm, setShowAddForm] = useState(false);
  // Spec 011 — before/after photo slots. draftId correlates picked photos to
  // this in-progress entry (see photoQueue.ts/serviceQueue.ts); a fresh one
  // is minted each time the form opens so a previous session's now-orphaned
  // draftId is never reused.
  const [photoDraftId, setPhotoDraftId] = useState(() => crypto.randomUUID());
  const [photoSlots, setPhotoSlots] = useState<BeforeAfterPhotoValue>(EMPTY_PHOTO_SLOTS);
  // Spec 011 Slice 2 — the hours value last WRITTEN into the field as a
  // suggestion (not merely "the current suggestion"), so submit-time can
  // tell "accepted unedited" from "edited" even if the suggestion itself
  // has since changed (e.g. a photo was swapped) without the owner touching
  // the field again. `null` = no suggestion has ever been auto-filled this
  // session — required for the "typed manually, no suggestion offered"
  // case to correctly omit `laborHoursSource` (table row 3).
  const [lastSuggestedHours, setLastSuggestedHours] = useState<number | null>(null);

  const today = format(new Date(), 'yyyy-MM-dd');

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm<ServiceEntryFormInput, unknown, ServiceEntryFormOutput>({
    resolver: zodResolver(ServiceEntryFormSchema),
    defaultValues: { performedAt: today, description: '', selfPerformed: false },
  });

  // Spec 011 Slice 2 — reactive read for the "Edited — was Xh" label below,
  // via useWatch (a real hook) rather than the `watch()` function returned
  // by useForm (React Compiler can't safely memoize that pattern). The
  // auto-fill effect itself uses getValues() (a point read, not a
  // subscription) so it only runs when the suggestion changes, not on every
  // keystroke.
  const watchedLaborHours = useWatch({ control, name: 'laborHours' });
  const currentLaborHoursNum = toNumberOrNull(watchedLaborHours);
  const laborHoursWasEdited =
    lastSuggestedHours !== null &&
    currentLaborHoursNum !== null &&
    currentLaborHoursNum !== lastSuggestedHours;

  // Spec 011 Slice 2 — auto-fill the Labor hours field the moment a
  // suggestion becomes available, but ONLY while the field is empty or
  // still holds a previously auto-filled (untouched) suggestion — an
  // owner's own manual entry, or an edit, is never overwritten. Fires on
  // suggestion changes (a new pick, or a Swap), not on every keystroke.
  useEffect(() => {
    if (photoSlots.suggestion.status !== 'suggested') return;
    const { hours } = photoSlots.suggestion;
    const current = toNumberOrNull(getValues('laborHours'));
    const untouched = current === null || current === lastSuggestedHours;
    if (untouched) {
      setValue('laborHours', hours, { shouldValidate: false, shouldDirty: false });
      setLastSuggestedHours(hours);
    }
    // Deliberately narrow: only re-run when the suggestion itself changes —
    // getValues/setValue are stable RHF handles, and lastSuggestedHours is
    // read (not depended on) so an already-applied suggestion doesn't loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoSlots.suggestion]);

  // Stable IDs for aria-describedby associations.
  const baseId = useId();
  const descriptionErrorId = `${baseId}-description-error`;
  const performedAtErrorId = `${baseId}-performedAt-error`;
  const mileageErrorId = `${baseId}-mileage-error`;
  const laborHoursErrorId = `${baseId}-laborHours-error`;
  const addFormSheetId = `${baseId}-add-form-sheet`;

  // Focus management ref — for returning focus to the Add entry button on close.
  const addButtonRef = useRef<HTMLButtonElement>(null);
  // Guards the deferred initial-focus rAF below against stealing focus from
  // a user who has already moved into the form themselves.
  const formRef = useRef<HTMLFormElement>(null);
  // Incrementing counter: bumped whenever we want to return focus to addButtonRef.
  // A useEffect watching this value fires the focus() outside of render/submit
  // callbacks, which avoids the react-hooks/refs lint rule.
  const [returnFocusCount, setReturnFocusCount] = useState(0);

  // Effect: return focus to the Add entry button whenever returnFocusCount bumps.
  useEffect(() => {
    if (returnFocusCount === 0) return;
    requestAnimationFrame(() => {
      addButtonRef.current?.focus();
    });
  }, [returnFocusCount]);

  function openAddForm() {
    setShowAddForm(true);
    // Fresh draftId + empty slots each time the form opens (spec 011).
    setPhotoDraftId(crypto.randomUUID());
    setPhotoSlots(EMPTY_PHOTO_SLOTS);
    setLastSuggestedHours(null);
    // Move focus into the first field on next paint (after the form mounts).
    // Deferred by a full frame — under main-thread contention (slow device,
    // render backlog) this can fire AFTER a faster user has already clicked
    // or tabbed into a different field and started typing, yanking focus
    // (and their keystrokes) back to `description` mid-input. Guard: only
    // claim focus if it hasn't already landed inside the form.
    requestAnimationFrame(() => {
      if (!formRef.current?.contains(document.activeElement)) {
        setFocus('description');
      }
    });
  }

  function closeAddForm() {
    setShowAddForm(false);
    setShowInvoiceFlow(false);
    reset({ performedAt: today, description: '', selfPerformed: false });
    setPhotoSlots(EMPTY_PHOTO_SLOTS);
    setLastSuggestedHours(null);
    setSubmitError(null);
    // Signal the focus-return effect — does not touch addButtonRef during render.
    setReturnFocusCount((c) => c + 1);
  }

  const onSubmit = handleSubmit(async (data) => {
    if (!user || !vehicleId) return;
    setSubmitError(null);
    const { entryFields, pendingPhotoRefs } = toEntryPhotoFields(photoSlots);

    // Spec 011 Slice 2 — laborHoursSource, per the frozen suggestion-vs-
    // override table:
    //   accepted unedited  -> suggested value,        "photo_estimated"
    //   edited              -> edited value,            "edited"
    //   typed, no suggestion offered -> typed value,     omit (-> "manual")
    //   no suggestion possible       -> manual/empty,    omit (-> "manual")
    //   cleared                      -> omit laborHours, omit
    // `data.laborHours` is already coerced to a number (or '' / undefined)
    // by the zod resolver, so a direct `===` compare against
    // `lastSuggestedHours` is exact — no re-parsing needed.
    //
    // False-label fix (task #43, founder-approved, contract frozen by
    // lamport) — the server now rejects "photo_estimated"/"edited" unless
    // BOTH before/after photo refs are present, because a sealed entry could
    // otherwise carry a provenance label the underlying photos no longer
    // back (deleted or swapped after the suggestion was accepted). This is
    // the matching client-side gate, so the client never sends a label the
    // server will reject and never seals a false one:
    //   - "photo_estimated" requires BOTH (a) the suggestion is STILL
    //     `status === 'suggested'` at submit (a deleted/swapped/EXIF-less
    //     photo drops it to 'pending'/'unavailable') AND (b) both slots are
    //     populated (`photoSlots.before`/`.after` non-null — covers both the
    //     immediate-POST case, where the id is already resolved into
    //     `entryFields`, and the offline-queued case, where it's still a
    //     pending ref useServiceQueue.ts's drain resolves later — that drain
    //     carries its own downgrade-on-abandonment gate for that path).
    //   - "edited" only needs (b) — the owner's own typed number is truthful
    //     regardless of whether the suggestion itself is still live.
    //   - Otherwise: omit (-> "manual"), which needs no photos.
    const bothPhotoSlotsPopulated = photoSlots.before !== null && photoSlots.after !== null;
    const laborHoursValue = data.laborHours;
    const laborHoursSource: 'photo_estimated' | 'edited' | undefined =
      laborHoursValue === undefined || lastSuggestedHours === null || !bothPhotoSlotsPopulated
        ? undefined
        : laborHoursValue === lastSuggestedHours && photoSlots.suggestion.status === 'suggested'
          ? 'photo_estimated'
          : laborHoursValue !== lastSuggestedHours
            ? 'edited'
            : undefined;

    try {
      await add({
        vehicleId,
        entry: {
          summary: data.description,
          performedAt: data.performedAt,
          source: 'manual',
          ...(data.mileage !== undefined ? { mileage: data.mileage } : {}),
          ...(data.performedBy ? { performedBy: data.performedBy } : {}),
          ...(laborHoursValue !== undefined ? { laborHours: laborHoursValue } : {}),
          ...(laborHoursSource ? { laborHoursSource } : {}),
          ...(data.selfPerformed ? { selfPerformed: true } : {}),
          ...entryFields,
        },
        ...(pendingPhotoRefs.before || pendingPhotoRefs.after ? { pendingPhotoRefs } : {}),
      });
      // A still-resolving before/after photo means add() deferred the actual
      // POST to the queue drain — kick it now rather than waiting for the
      // next reconnect/focus/30s-tick trigger (mirrors PhotoAlbumSection's
      // enqueue-then-void-drain() pattern for the photo queue itself).
      void drain();
      // Collapse on success via closeAddForm() — restores focus to the Add-entry
      // button so keyboard/screen-reader users are not left on <body>.
      closeAddForm();
    } catch (err) {
      // Stay open on error so the user can see the problem.
      setSubmitError(err instanceof Error ? err.message : 'Could not save entry');
    }
  });

  // Dedup: a pending item disappears once its sealed twin arrives.
  // Sealed entries use clientMutationId as their server-assigned id (for
  // entries created via this path), so we compare sealed.id against
  // pending.clientMutationId.
  const sealedIds = new Set(entries.map((e) => e.id));
  const unseenPending = queuedEntries.filter((q) => !sealedIds.has(q.clientMutationId));

  // "Syncing" pill is driven by queue length, not Firestore hasPendingWrites
  // (that signal is gone now that writes go through the server).
  const hasPendingQueue = unseenPending.length > 0;

  return (
    <section>
      <BackLink to={`/vehicles/${vehicleId}`}>← vehicle</BackLink>

      {/* Header row: h1 + Syncing pill + Add entry CTA */}
      <PageHeader
        title="Service log"
        syncing={hasPendingQueue}
        actions={
          <Button
            ref={addButtonRef}
            size="sm"
            aria-expanded={showAddForm}
            aria-controls={addFormSheetId}
            onClick={openAddForm}
            disabled={showAddForm}
          >
            Add entry
          </Button>
        }
      />

      {/* Inline sheet — rendered when Add entry is open */}
      {showAddForm ? (
        <Card id={addFormSheetId} style={{ marginTop: SPACE.lg }}>
          <Heading level={2} size="md" style={{ marginBottom: SPACE.sm }}>
            Add entry
          </Heading>
          <div style={{ marginBottom: SPACE.md }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowInvoiceFlow(true)}
              disabled={showInvoiceFlow}
            >
              Add from photo / scan
            </Button>
          </div>
          {showInvoiceFlow && user ? (
            <InvoiceFlow
              vehicleId={vehicleId}
              authorUid={user.uid}
              onDone={() => setShowInvoiceFlow(false)}
              onCancel={() => setShowInvoiceFlow(false)}
            />
          ) : null}
          <form ref={formRef} onSubmit={onSubmit} style={{ display: 'grid', gap: SPACE.md }}>
            <Field
              label="What did you do?"
              error={errors.description?.message}
              errorId={descriptionErrorId}
            >
              <textarea
                rows={3}
                style={inputStyle}
                {...register('description')}
                placeholder="Replaced front brake pads"
                aria-describedby={errors.description ? descriptionErrorId : undefined}
                aria-invalid={!!errors.description}
              />
            </Field>
            <Field
              label="Performed at"
              error={errors.performedAt?.message}
              errorId={performedAtErrorId}
            >
              <input
                type="date"
                style={inputStyle}
                {...register('performedAt')}
                aria-describedby={errors.performedAt ? performedAtErrorId : undefined}
                aria-invalid={!!errors.performedAt}
              />
            </Field>
            <Field label="Mileage" error={errors.mileage?.message} errorId={mileageErrorId}>
              <input
                type="number"
                inputMode="numeric"
                style={inputStyle}
                {...register('mileage')}
                aria-describedby={errors.mileage ? mileageErrorId : undefined}
                aria-invalid={!!errors.mileage}
              />
            </Field>
            <Field label="Performed by">
              <input type="text" style={inputStyle} {...register('performedBy')} />
            </Field>
            <Field
              label="Labor hours"
              error={errors.laborHours?.message}
              errorId={laborHoursErrorId}
            >
              <input
                type="number"
                step="0.25"
                inputMode="decimal"
                style={inputStyle}
                {...register('laborHours')}
                aria-describedby={errors.laborHours ? laborHoursErrorId : undefined}
                aria-invalid={!!errors.laborHours}
              />
            </Field>
            {/* Spec 011 Slice 2 — a quiet, editable provenance line under the
                Labor hours field (eames' design), never an imposed value.
                role="status"/aria-live so screen readers hear the estimate
                land without stealing focus. */}
            {photoSlots.suggestion.status === 'suggested' ? (
              <p
                role="status"
                aria-live="polite"
                style={{
                  margin: 0,
                  fontSize: 12,
                  fontFamily: F.body,
                  color: T.textMuted,
                }}
              >
                {laborHoursWasEdited && lastSuggestedHours !== null
                  ? `Edited — was ${formatHoursAsHm(lastSuggestedHours)} from photo timestamps`
                  : `Estimated from photo timestamps · ${formatHoursAsHm(photoSlots.suggestion.hours)}`}
              </p>
            ) : photoSlots.suggestion.status === 'unavailable' ? (
              <p
                role="status"
                aria-live="polite"
                style={{
                  margin: 0,
                  fontSize: 12,
                  fontFamily: F.body,
                  color: T.textMuted,
                }}
              >
                Couldn't estimate a duration from these photos — enter it manually.
              </p>
            ) : null}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACE.sm,
                fontSize: 13,
                fontFamily: F.body,
                color: T.text,
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              <input
                type="checkbox"
                {...register('selfPerformed')}
                style={{ width: 18, height: 18, accentColor: T.accent }}
              />
              I did this work myself
            </label>
            {/* Not a <Field> — Field wraps children in a single <label>, which
                is the wrong semantics for a composite widget with its own
                multiple interactive controls (slots, Swap, delete). */}
            <div>
              <p
                style={{
                  margin: `0 0 ${SPACE.sm}px`,
                  fontSize: 12,
                  color: T.textMuted,
                  letterSpacing: '0.04em',
                }}
              >
                Photos (optional)
              </p>
              <BeforeAfterPhotoSlots
                vehicleId={vehicleId}
                draftId={photoDraftId}
                disabled={addPending}
                onChange={setPhotoSlots}
              />
            </div>
            {submitError ? (
              <p role="alert" style={{ color: T.red, fontSize: 13, margin: 0 }}>
                {submitError}
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: SPACE.sm }}>
              <Button type="submit" loading={addPending}>
                Save entry
              </Button>
              <Button type="button" variant="secondary" onClick={closeAddForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* History — ledger table (always visible, default top content)       */}
      {/* ------------------------------------------------------------------ */}
      <div style={{ marginTop: SPACE.lg }}>
        <Heading level={2} size="md">
          History
        </Heading>
        {loading ? (
          <div style={{ marginTop: SPACE.md }}>
            <SkeletonList count={2} />
          </div>
        ) : error ? (
          <div style={{ marginTop: SPACE.md }}>
            <Card accent="red">
              <p style={{ margin: 0, fontSize: 14, color: T.text }}>
                Couldn't load service log: {error.message}
              </p>
            </Card>
          </div>
        ) : entries.length === 0 && unseenPending.length === 0 ? (
          <div style={{ marginTop: SPACE.md }}>
            <EmptyState hint="Add the first entry above to start the record.">
              No entries yet.
            </EmptyState>
          </div>
        ) : (
          <div
            style={{
              marginTop: SPACE.md,
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md,
              overflow: 'hidden',
            }}
          >
            {/* Column header row */}
            <div
              aria-hidden="true"
              style={{
                display: 'grid',
                gridTemplateColumns: '96px 1fr 80px',
                gap: `0 ${SPACE.sm}px`,
                // Intentional optical padding for the column header — lighter than row padding.
                padding: `6px ${SPACE.md}px`,
                borderBottom: `1px solid ${T.border}`,
                background: `color-mix(in srgb, var(--ap-parchment) 60%, transparent)`,
              }}
            >
              <span style={colHeaderStyle}>Date</span>
              <span style={colHeaderStyle}>Event</span>
              <span style={{ ...colHeaderStyle, textAlign: 'right' }}>Verified</span>
            </div>

            {/* Queued / provisional rows — not navigable */}
            {unseenPending.map((q) => {
              const isFailed = q.status === 'failed';
              return (
                <div
                  key={q.clientMutationId}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '96px 1fr 80px',
                    gap: `0 ${SPACE.sm}px`,
                    padding: `${LEDGER_ROW_PADDING_Y}px ${SPACE.md}px`,
                    borderBottom: `1px solid ${T.border}`,
                    alignItems: 'start',
                    opacity: isFailed ? 0.75 : 1,
                  }}
                >
                  {/* Date cell */}
                  <div style={dateCellStyle}>{ledgerDate(q.entry.performedAt)}</div>

                  {/* Event cell */}
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: T.textMuted,
                        fontStyle: 'italic',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {q.entry.summary?.trim() || ledgerTitleFallback(q.entry)}
                    </div>
                    <div style={eventSubStyle}>
                      {typeof q.entry.mileage === 'number'
                        ? `${q.entry.mileage.toLocaleString()} mi`
                        : null}
                      {typeof q.entry.laborHours === 'number' && q.entry.mileage !== undefined
                        ? ' · '
                        : null}
                      {typeof q.entry.laborHours === 'number' ? `${q.entry.laborHours} h` : null}
                    </div>
                    {/* Status + actions */}
                    <div
                      style={{
                        marginTop: SPACE.xs,
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACE.sm,
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* Canonical ledger inline status tag — "Not synced" / "Not saved".
                          This matched pair is intentional: mono, small, rounded, tinted
                          orange (caution) or red (failed). Do NOT replace with <Pill>;
                          the ledger tag is a distinct lower-density element. */}
                      <span
                        style={{
                          fontFamily: F.mono,
                          fontSize: 10,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: isFailed ? T.red : T.orange,
                          border: `1px solid ${isFailed ? `color-mix(in srgb, ${T.red} 40%, transparent)` : `color-mix(in srgb, ${T.orange} 40%, transparent)`}`,
                          padding: '2px 6px',
                          borderRadius: RADIUS.sm,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isFailed ? 'Not saved' : 'Not synced'}
                      </span>
                      {/* Status copy — tested by ServiceLog.test.tsx */}
                      <span
                        style={{
                          fontFamily: F.mono,
                          fontSize: 10,
                          color: T.textMuted,
                          letterSpacing: '0.03em',
                          display: 'block',
                          marginTop: 3,
                        }}
                      >
                        {isFailed
                          ? q.failureReason === 'photo_not_ready'
                            ? PHOTO_NOT_READY_MESSAGE
                            : 'This entry was NOT saved to the record.'
                          : 'Still syncing, not yet in the record'}
                      </span>
                      {isFailed ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              void removeQueued(q.clientMutationId);
                            }}
                          >
                            Discard
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              void drain();
                            }}
                          >
                            Retry
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>

                  {/* Verified cell — pending rows never verified */}
                  <div style={{ textAlign: 'right' }} />
                </div>
              );
            })}

            {/* Sealed (server-committed) rows */}
            {entries.map((e, idx) => {
              // Spec 006 FR-005: privacy-bounded authorship.
              const isMine = !!e.createdBy && !!user?.uid && e.createdBy === user.uid;
              const authorshipLabel = isMine ? 'Logged by you' : 'Logged by previous owner';
              const isLast = idx === entries.length - 1 && unseenPending.length === 0;
              return (
                <Link
                  key={e.id}
                  to={`/vehicles/${vehicleId}/service/${e.id}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '96px 1fr 80px',
                    gap: `0 ${SPACE.sm}px`,
                    padding: `${LEDGER_ROW_PADDING_Y}px ${SPACE.md}px`,
                    borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
                    alignItems: 'start',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: `background ${TRANSITION.fast}`,
                  }}
                  onMouseEnter={(ev: React.MouseEvent<HTMLAnchorElement>) => {
                    (ev.currentTarget as HTMLElement).style.background =
                      `color-mix(in srgb, ${T.accent} 4%, transparent)`;
                  }}
                  onMouseLeave={(ev: React.MouseEvent<HTMLAnchorElement>) => {
                    (ev.currentTarget as HTMLElement).style.background = '';
                  }}
                >
                  {/* Date cell */}
                  <div style={dateCellStyle}>{ledgerDate(e.performedAt)}</div>

                  {/* Event cell */}
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: e.summary?.trim() ? T.text : T.textMuted,
                        fontStyle: e.summary?.trim() ? 'normal' : 'italic',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {e.summary?.trim() || ledgerTitleFallback(e)}
                    </div>
                    <div style={eventSubStyle}>
                      {typeof e.mileage === 'number' ? `${e.mileage.toLocaleString()} mi` : null}
                      {typeof e.mileage === 'number' && typeof e.laborHours === 'number'
                        ? ' · '
                        : null}
                      {typeof e.laborHours === 'number' ? `${e.laborHours} h` : null}
                      {(e.mileage !== undefined || e.laborHours !== undefined) &&
                      authorshipLabel ? (
                        <span style={{ marginLeft: SPACE.xs }}>· {authorshipLabel}</span>
                      ) : (
                        <span>{authorshipLabel}</span>
                      )}
                    </div>
                  </div>

                  {/* Verified cell — sealed entries are on the record */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        fontFamily: F.mono,
                        fontSize: 10,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: 'var(--ap-good)',
                        whiteSpace: 'nowrap',
                      }}
                      title="Entry is on the sealed record"
                    >
                      <Check size={11} aria-hidden="true" strokeWidth={2.5} />
                      <span className="sr-only">Verified</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// Intentional optical padding for ledger rows — 11px top/bottom gives a
// comfortable reading rhythm against the 1px rule lines without looking
// as open as the 16px card padding. Do NOT "fix" these to SPACE.sm.
// Named constant so linters don't flag the literal and reviewers see intent.
const LEDGER_ROW_PADDING_Y = 11;

// Ledger column header label style
const colHeaderStyle: React.CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: T.textMuted,
};

// Date cell — mono, tabular-nums, muted
// performedAt may be a date (`2026-06-13`) or a full ISO datetime
// (`2026-02-26T00:00:00Z`, from extraction-derived entries). The ledger column
// shows the calendar day only — slicing to the first 10 chars handles both and
// keeps the fixed-width date column from overflowing into the event column.
const ledgerDate = (s?: string): string => (s ? s.slice(0, 10) : '—');

// Every ledger row needs a title line. Manual entries can have an empty summary
// (just mileage/hours); fall back to a neutral label (the vendor if present,
// else a generic one) shown muted so it reads as a system label, not a title.
const ledgerTitleFallback = (e: { vendor?: string }): string => e.vendor?.trim() || 'Service entry';

const dateCellStyle: React.CSSProperties = {
  fontFamily: F.mono,
  fontVariantNumeric: 'tabular-nums',
  fontSize: 12,
  color: T.textMuted,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.3,
  paddingTop: 1,
};

// Sub-row below event title — mileage, hours, authorship
const eventSubStyle: React.CSSProperties = {
  fontFamily: F.mono,
  fontVariantNumeric: 'tabular-nums',
  fontSize: 12,
  color: T.textMuted,
  marginTop: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
